import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowLeft, MapPin, Heart, Users, Droplets, ChevronRight, Play, Camera, Bird, Globe, Mail, Check } from "lucide-react";

const CYAN = "#07D4D4";
const CORAL = "#FF6B35";

const stats = [
  { value: "71%", label: "of Earth is ocean" },
  { value: "8M", label: "tons of plastic dumped annually" },
  { value: "50%", label: "of coral reefs lost since 1950" },
  { value: "90%", label: "of large fish populations depleted" },
];

const brandColors = [
  { hex: "#020D1A", name: "Abyss", role: "Primary Background" },
  { hex: "#07D4D4", name: "Bioluminescence", role: "Brand Primary" },
  { hex: "#0B5EA8", name: "Deep Current", role: "Brand Secondary" },
  { hex: "#FF6B35", name: "Coral Flame", role: "Accent / CTA" },
  { hex: "#E2F0F5", name: "Sea Mist", role: "Text / Light" },
  { hex: "#5E8FA5", name: "Horizon", role: "Muted Text" },
];

const deliverables = [
  { icon: Globe, label: "Campaign Landing Page", desc: "Impact-first hero, donation flow, volunteer sign-up, map" },
  { icon: Heart, label: "Donation Flow", desc: "3-step frictionless giving experience with impact preview" },
  { icon: Users, label: "Volunteer Sign-Up", desc: "Location-based matching with ocean zone assignments" },
  { icon: Camera, label: "Social Media Kit", desc: "32 assets across Instagram, Twitter, Facebook, Stories" },
];

const donationSteps = [
  { step: "01", title: "Choose Your Impact", desc: "Select a cause area — coral restoration, plastic removal, or marine reserve funding." },
  { step: "02", title: "Set Your Contribution", desc: "One-time or recurring. See real-time equivalence: $25 = 50 lbs of ocean plastic removed." },
  { step: "03", title: "Join the Movement", desc: "Receive your digital ocean guardian badge and monthly impact report." },
];

const mapZones = [
  { x: 20, y: 38, label: "Pacific Trash Vortex", status: "critical", size: 28 },
  { x: 52, y: 55, label: "Atlantic Coral Belt", status: "protected", size: 18 },
  { x: 67, y: 42, label: "Indian Ocean Reserve", status: "monitoring", size: 22 },
  { x: 35, y: 65, label: "Southern Ocean Sanctuary", status: "protected", size: 16 },
  { x: 80, y: 30, label: "Coral Triangle", status: "critical", size: 20 },
];

const socialAssets = [
  {
    url: "https://images.unsplash.com/photo-1637308107080-82cf40953742?w=400&h=400&fit=crop&auto=format",
    caption: "Every wave carries a choice. Make yours count. #ProjectOcean",
    platform: "Instagram",
    type: "Square",
  },
  {
    url: "https://images.unsplash.com/photo-1526951521990-620dc14c214b?w=400&h=400&fit=crop&auto=format",
    caption: "8 million tons. Every. Single. Year. The ocean needs you now.",
    platform: "Twitter",
    type: "Banner",
  },
  {
    url: "https://images.unsplash.com/photo-1437622368342-7a3d73a34c8f?w=400&h=400&fit=crop&auto=format",
    caption: "Coral reefs support 25% of all marine life. Half are already gone.",
    platform: "Instagram",
    type: "Story",
  },
  {
    url: "https://images.unsplash.com/photo-1682687982049-b3d433368cd1?w=400&h=400&fit=crop&auto=format",
    caption: "Join 140,000 ocean guardians protecting our blue planet.",
    platform: "Facebook",
    type: "Cover",
  },
];

const outcomes = [
  { metric: "140K", label: "Campaign Signups", change: "+340%" },
  { metric: "$2.4M", label: "Donations Raised", change: "+180%" },
  { metric: "28M", label: "Social Impressions", change: "+520%" },
  { metric: "94%", label: "Donor Retention", change: "+22%" },
];

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

function FadeIn({ children, delay = 0, className = "" }) {
  const { ref, inView } = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(32px)",
        transition: `opacity 0.8s ease ${delay}s, transform 0.8s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

function Tag({ children, color = CYAN }) {
  return (
    <span
      className="inline-block text-[10px] font-mono font-medium tracking-[0.2em] uppercase px-3 py-1 rounded-sm"
      style={{ color, border: `1px solid ${color}30`, background: `${color}10` }}
    >
      {children}
    </span>
  );
}

export default function Ocean() {
  const [activeDonation, setActiveDonation] = useState(0);
  const [donationAmt, setDonationAmt] = useState(50);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [hoveredZone, setHoveredZone] = useState(null);

  const zoneColor = (s) =>
    s === "critical" ? CORAL : s === "protected" ? CYAN : "#F0C040";

  return (
    <div
      className="min-h-screen overflow-x-hidden"
      style={{
        background: "#020D1A",
        fontFamily: "'Outfit', sans-serif",
        color: "#E2F0F5",
        scrollbarWidth: "none",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;0,900;1,400;1,700&family=Outfit:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        ::-webkit-scrollbar { display: none; }
        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        @keyframes pulse-ring { 0%{transform:scale(1);opacity:0.6} 100%{transform:scale(2.2);opacity:0} }
        @keyframes shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
        .font-display { font-family: 'Playfair Display', serif; }
        .font-mono-dm { font-family: 'DM Mono', monospace; }
        .shimmer-text {
          background: linear-gradient(90deg, #07D4D4 0%, #0B5EA8 30%, #E2F0F5 50%, #07D4D4 70%, #0B5EA8 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: shimmer 4s linear infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after { animation-duration: 0.01ms !important; scroll-behavior: auto !important; transition-duration: 0.01ms !important; }
        }
      `}</style>

      {/* ── NAV ── */}
      <nav aria-label="Case study navigation" className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between gap-4 px-4 md:px-8 py-4"
        style={{ background: "linear-gradient(to bottom, #020D1Aee, transparent)" }}>
        <div className="flex items-center gap-3 min-w-0">
        <Link to="/" className="flex items-center gap-2 text-xs font-medium whitespace-nowrap" style={{ color: "#E2F0F5" }}>
          <ArrowLeft size={15} aria-hidden="true" />
          <span className="hidden sm:inline">Portfolio</span>
        </Link>
          <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: `${CYAN}20`, border: `1.5px solid ${CYAN}` }}>
            <Droplets size={14} style={{ color: CYAN }} aria-hidden="true" />
          </div>
          <span className="font-mono-dm text-xs tracking-widest uppercase whitespace-nowrap" style={{ color: CYAN }}>Project Ocean</span>
        </div>
        <div className="hidden md:flex items-center gap-8">
          {["Overview", "Brand", "Screens", "Outcomes"].map(l => (
            <a key={l} href={`/ocean#${l.toLowerCase()}`}
              className="text-xs tracking-widest uppercase transition-colors duration-300"
              style={{ color: "#5E8FA5", fontFamily: "'DM Mono', monospace" }}
              onMouseEnter={e => (e.currentTarget.style.color = CYAN)}
              onMouseLeave={e => (e.currentTarget.style.color = "#5E8FA5")}
            >{l}</a>
          ))}
        </div>
      </nav>

      {/* ── HERO ── */}
      <main id="case-study-content">
      <section className="relative h-screen flex flex-col items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1518020382113-a7e8fc38eac9?w=1800&h=1000&fit=crop&auto=format"
            alt="Deep ocean cinematic view"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, #020D1Acc 0%, #020D1A88 40%, #020D1Acc 80%, #020D1A 100%)" }} />
          <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at 50% 40%, ${CYAN}08 0%, transparent 70%)` }} />
        </div>

        <div className="relative z-10 text-center px-8 max-w-5xl mx-auto">
          <div className="mb-8 flex items-center justify-center gap-4">
            <div className="h-px w-16" style={{ background: `linear-gradient(to right, transparent, ${CYAN})` }} />
            <Tag>Behance Portfolio Case Study · 2025</Tag>
            <div className="h-px w-16" style={{ background: `linear-gradient(to left, transparent, ${CYAN})` }} />
          </div>
          <h1 className="font-display text-[clamp(3rem,8vw,7rem)] font-900 leading-[0.92] tracking-tight mb-6">
            <span className="block shimmer-text">Project Ocean</span>
            <span className="block italic font-400 text-[clamp(1.8rem,4vw,3.5rem)] mt-3" style={{ color: "#C5DEE8" }}>
              Protect Our Blue Planet
            </span>
          </h1>
          <p className="text-base md:text-lg max-w-xl mx-auto leading-relaxed mb-12" style={{ color: "#6B9BB5" }}>
            A full-scale non-profit awareness campaign — from brand identity to donation flows —
            crafted to move hearts and mobilize action for ocean conservation.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <div
              className="cursor-pointer flex items-center gap-2 px-7 py-3.5 text-sm font-medium tracking-wide transition-all duration-300 hover:scale-105"
              style={{ background: CYAN, color: "#020D1A", borderRadius: "2px" }}
            >
              <Play size={14} fill="#020D1A" aria-hidden="true" />
              View Campaign
            </div>
            <div
              className="flex cursor-pointer items-center gap-2 px-7 py-3.5 text-sm font-medium tracking-wide transition-all duration-300 hover:scale-105"
              style={{ border: `1px solid ${CYAN}40`, color: CYAN, borderRadius: "2px", background: `${CYAN}08` }}
            >
              Case Study
              <ChevronRight size={14} />
            </div>
          </div>
        </div>

        <div className="cursor-pointer  absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-50" style={{ animation: "float 2.5s ease-in-out infinite" }}>
          <ArrowDown size={18} style={{ color: CYAN }} />
          <span className="font-mono-dm text-[10px] tracking-widest uppercase" style={{ color: CYAN }}>Scroll</span>
        </div>
      </section>

      {/* ── PROJECT OVERVIEW ── */}
      <section id="overview" className="px-8 py-28 max-w-6xl mx-auto">
        <FadeIn>
          <Tag>Project Overview</Tag>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 mt-10">
            <div>
              <h2 className="font-display text-4xl md:text-5xl font-700 leading-tight mb-6">
                The ocean is<br />
                <span className="italic" style={{ color: CYAN }}>calling for help.</span>
              </h2>
              <p className="text-base leading-relaxed mb-6" style={{ color: "#5E8FA5" }}>
                Project Ocean commissioned a full brand and digital campaign to spark urgency
                around marine conservation. The brief: tell a story so compelling that a first-time
                visitor becomes a lifelong advocate — within one scroll.
              </p>
              <p className="text-base leading-relaxed" style={{ color: "#5E8FA5" }}>
                My role spanned brand strategy, UI/UX design, motion direction, and social content
                production — delivered across a 10-week sprint with a team of three.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: "Role", value: "Lead Brand & UX Designer" },
                { label: "Timeline", value: "10 Weeks · 2025" },
                { label: "Client", value: "Project Ocean NGO" },
                { label: "Tools", value: "Figma, Framer, Illustrator" },
              ].map(({ label, value }) => (
                <div key={label} className="p-5 rounded-sm" style={{ background: "#041829", border: "1px solid rgba(7,212,212,0.1)" }}>
                  <div className="font-mono-dm text-[10px] tracking-widest uppercase mb-2" style={{ color: "#5E8FA5" }}>{label}</div>
                  <div className="text-sm font-medium" style={{ color: "#E2F0F5" }}>{value}</div>
                </div>
              ))}
            </div>
          </div>
        </FadeIn>

        {/* Stats bar */}
        <FadeIn delay={0.1} className="mt-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px rounded-sm overflow-hidden" style={{ border: "1px solid rgba(7,212,212,0.1)" }}>
            {stats.map(({ value, label }, i) => (
              <div key={i} className="px-8 py-10 text-center" style={{ background: "#041829" }}>
                <div className="font-display text-4xl md:text-5xl font-900 mb-2" style={{ color: CYAN }}>{value}</div>
                <div className="text-xs tracking-wide" style={{ color: "#5E8FA5" }}>{label}</div>
              </div>
            ))}
          </div>
        </FadeIn>
      </section>

      {/* ── DESIGN CHALLENGE ── */}
      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1800&h=700&fit=crop&auto=format"
            alt="Ocean waves at dusk"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to right, #020D1A 0%, transparent 40%, transparent 60%, #020D1A 100%)" }} />
        </div>
        <FadeIn className="relative z-10 px-8 max-w-4xl mx-auto text-center">
          <Tag>Design Challenge</Tag>
          <h2 className="font-display text-4xl md:text-6xl font-700 mt-8 mb-8 leading-tight">
            Evoke emotion.<br />
            <span className="italic" style={{ color: CORAL }}>Demand action.</span>
          </h2>
          <p className="text-lg leading-relaxed max-w-2xl mx-auto" style={{ color: "#6B9BB5" }}>
            The tension: conservation campaigns often choose between raw emotional impact
            and polished credibility. We refused to choose. The design system had to feel
            as vast and urgent as the ocean itself — while earning the trust of first-time donors.
          </p>
        </FadeIn>
      </section>

      {/* ── BRAND IDENTITY ── */}
      <section id="brand" className="px-8 py-28 max-w-6xl mx-auto">
        <FadeIn>
          <Tag>Brand Identity</Tag>
          <h2 className="font-display text-4xl md:text-5xl font-700 mt-6 mb-16 leading-tight">
            A language as deep<br />as <span className="italic" style={{ color: CYAN }}>the sea itself.</span>
          </h2>
        </FadeIn>

        {/* Color Palette */}
        <FadeIn delay={0.1}>
          <div className="mb-4 font-mono-dm text-[10px] tracking-widest uppercase" style={{ color: "#5E8FA5" }}>Brand Palette</div>
          <div className="flex flex-wrap gap-3 mb-16">
            {brandColors.map(({ hex, name, role }) => (
              <div key={hex} className="flex-1 min-w-[120px] group cursor-default">
                <div
                  className="h-24 rounded-sm mb-3 transition-transform duration-300 group-hover:scale-105"
                  style={{ background: hex, border: hex === "#E2F0F5" ? "1px solid rgba(7,212,212,0.2)" : "none" }}
                />
                <div className="font-mono-dm text-[10px] tracking-wide mb-0.5" style={{ color: CYAN }}>{hex}</div>
                <div className="text-xs font-medium">{name}</div>
                <div className="text-[10px]" style={{ color: "#5E8FA5" }}>{role}</div>
              </div>
            ))}
          </div>
        </FadeIn>

        {/* Typography */}
        <FadeIn delay={0.15}>
          <div className="mb-4 font-mono-dm text-[10px] tracking-widest uppercase" style={{ color: "#5E8FA5" }}>Typography System</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
            <div className="p-8 rounded-sm" style={{ background: "#041829", border: "1px solid rgba(7,212,212,0.1)" }}>
              <div className="font-mono-dm text-[10px] tracking-widest uppercase mb-4" style={{ color: "#5E8FA5" }}>Display · Playfair Display</div>
              <div className="font-display text-5xl font-900 leading-none mb-4" style={{ color: CYAN }}>Aa</div>
              <div className="font-display text-2xl font-400 italic mb-2">Protect Our Blue Planet</div>
              <div className="font-display text-base">ABCDEFGHIJKLMNOPQRSTUVWXYZ</div>
              <div className="font-display text-base">abcdefghijklmnopqrstuvwxyz</div>
            </div>
            <div className="p-8 rounded-sm" style={{ background: "#041829", border: "1px solid rgba(7,212,212,0.1)" }}>
              <div className="font-mono-dm text-[10px] tracking-widest uppercase mb-4" style={{ color: "#5E8FA5" }}>Body · Outfit</div>
              <div className="text-5xl font-700 leading-none mb-4">Aa</div>
              <div className="text-2xl font-300 mb-2">Clean. Modern. Urgent.</div>
              <div className="text-base font-400" style={{ color: "#6B9BB5" }}>
                The ocean covers 71% of our planet and generates half the oxygen we breathe.
                Its health is our survival.
              </div>
            </div>
          </div>
        </FadeIn>

        {/* Logo Concept */}
        <FadeIn delay={0.2}>
          <div className="mb-4 font-mono-dm text-[10px] tracking-widest uppercase" style={{ color: "#5E8FA5" }}>Logo Mark</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { bg: "#020D1A", label: "Dark / Primary", border: `1px solid ${CYAN}30` },
              { bg: CYAN, label: "Reversed / Light" },
              { bg: "#0B2238", label: "Mid / Secondary", border: `1px solid ${CYAN}20` },
            ].map(({ bg, label, border }) => (
              <div key={label}
                className="p-10 rounded-sm flex flex-col items-center justify-center gap-5"
                style={{ background: bg, border: border || "none", minHeight: 180 }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: bg === CYAN ? "#020D1A20" : `${CYAN}20`, border: `2px solid ${bg === CYAN ? "#020D1A" : CYAN}` }}
                  >
                    <Droplets size={18} style={{ color: bg === CYAN ? "#020D1A" : CYAN }} />
                  </div>
                  <div>
                    <div className="font-display text-lg font-700 leading-none" style={{ color: bg === CYAN ? "#020D1A" : CYAN }}>PROJECT</div>
                    <div className="font-display text-lg font-400 italic leading-none" style={{ color: bg === CYAN ? "#020D1A80" : "#5E8FA5" }}>Ocean</div>
                  </div>
                </div>
                <div className="font-mono-dm text-[9px] tracking-widest uppercase" style={{ color: bg === CYAN ? "#020D1A80" : "#5E8FA5" }}>{label}</div>
              </div>
            ))}
          </div>
        </FadeIn>
      </section>

      {/* ── DELIVERABLES ── */}
      <section className="px-8 py-20 max-w-6xl mx-auto">
        <FadeIn>
          <Tag>Deliverables</Tag>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            {deliverables.map(({ icon: Icon, label, desc }, i) => (
              <FadeIn key={label} delay={i * 0.08}>
                <div
                  className="p-6 rounded-sm h-full group cursor-default transition-all duration-300 hover:-translate-y-1"
                  style={{ background: "#041829", border: "1px solid rgba(7,212,212,0.1)" }}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = `${CYAN}40`)}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = "rgba(7,212,212,0.1)")}
                >
                  <div className="w-10 h-10 rounded-sm flex items-center justify-center mb-4"
                    style={{ background: `${CYAN}15`, border: `1px solid ${CYAN}30` }}>
                    <Icon size={18} style={{ color: CYAN }} />
                  </div>
                  <div className="font-medium text-sm mb-2">{label}</div>
                  <div className="text-xs leading-relaxed" style={{ color: "#5E8FA5" }}>{desc}</div>
                </div>
              </FadeIn>
            ))}
          </div>
        </FadeIn>
      </section>

      {/* ── CAMPAIGN LANDING PAGE PREVIEW ── */}
      <section id="screens" className="px-8 py-20 max-w-6xl mx-auto">
        <FadeIn>
          <Tag>Campaign Landing Page</Tag>
          <h2 className="font-display text-4xl md:text-5xl font-700 mt-6 mb-12 leading-tight">
            The page that<br />
            <span className="italic" style={{ color: CYAN }}>starts the movement.</span>
          </h2>
        </FadeIn>

        {/* Landing page mockup */}
        <FadeIn delay={0.1}>
          <div className="rounded-sm overflow-hidden" style={{ border: "1px solid rgba(7,212,212,0.15)" }}>
            {/* Browser chrome */}
            <div className="flex items-center gap-2 px-5 py-3" style={{ background: "#041829", borderBottom: "1px solid rgba(7,212,212,0.1)" }}>
              <div className="w-3 h-3 rounded-full" style={{ background: CORAL }} />
              <div className="w-3 h-3 rounded-full" style={{ background: "#F0C040" }} />
              <div className="w-3 h-3 rounded-full" style={{ background: CYAN }} />
              <div className="flex-1 mx-4 px-4 py-1 rounded-sm text-[10px] font-mono-dm text-center" style={{ background: "#020D1A", color: "#5E8FA5" }}>
                projectocean.org
              </div>
            </div>
            {/* Hero preview */}
            <div className="relative h-80 overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1518020382113-a7e8fc38eac9?w=1200&h=600&fit=crop&auto=format"
                alt="Campaign landing page hero"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, #020D1Aee 0%, #020D1A88 100%)" }} />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8 gap-4">
                <span className="font-mono-dm text-[9px] tracking-widest uppercase px-3 py-1 rounded-sm"
                  style={{ background: `${CYAN}15`, color: CYAN, border: `1px solid ${CYAN}30` }}>
                  Global Campaign 2025
                </span>
                <h3 className="font-display text-4xl md:text-6xl font-900 leading-none">
                  <span className="shimmer-text">The Ocean Is</span><br />
                  <span className="italic font-400">Running Out of Time.</span>
                </h3>
                <p className="text-sm max-w-md" style={{ color: "#6B9BB5" }}>
                  Join 140,000 guardians protecting our last great wilderness.
                </p>
                <div className="flex gap-3 mt-2">
                  <div className="px-5 py-2.5 text-xs font-medium rounded-sm cursor-pointer " style={{ background: CYAN, color: "#020D1A" }}>Donate Now</div>
                  <div className="px-5 py-2.5 text-xs font-medium rounded-sm cursor-pointer " style={{ border: `1px solid ${CYAN}40`, color: CYAN }}>Volunteer</div>
                </div>
              </div>
            </div>
            {/* Stats strip in mockup */}
            <div className="grid grid-cols-4 gap-px" style={{ background: "rgba(7,212,212,0.08)" }}>
              {stats.map(({ value, label }) => (
                <div key={label} className="px-4 py-5 text-center" style={{ background: "#041829" }}>
                  <div className="font-display text-xl font-700" style={{ color: CYAN }}>{value}</div>
                  <div className="text-[9px] mt-1" style={{ color: "#5E8FA5" }}>{label}</div>
                </div>
              ))}
            </div>
          </div>
        </FadeIn>
      </section>

      {/* ── DONATION FLOW ── */}
      <section className="px-8 py-20 max-w-6xl mx-auto">
        <FadeIn>
          <Tag>Donation Flow</Tag>
          <h2 className="font-display text-4xl md:text-5xl font-700 mt-6 mb-12 leading-tight">
            Frictionless giving,<br />
            <span className="italic" style={{ color: CORAL }}>maximum impact.</span>
          </h2>
        </FadeIn>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          {/* Step tabs */}
          <FadeIn delay={0.1}>
            <div className="flex gap-2 mb-6">
              {donationSteps.map((s, i) => (
                <div
                  key={i}
                  onClick={() => setActiveDonation(i)}
                  type="button"
                  role="tab"
                  aria-selected={activeDonation === i}
                  aria-controls="donation-step-panel"
                  className="font-mono-dm text-[10px] tracking-widest uppercase px-4 py-2 rounded-sm transition-all duration-300 cursor-pointer hover:-translate-y-0.5"
                  style={{
                    background: activeDonation === i ? CYAN : "transparent",
                    color: activeDonation === i ? "#020D1A" : "#5E8FA5",
                    border: `1px solid ${activeDonation === i ? CYAN : "rgba(7,212,212,0.2)"}`,
                  }}
                >
                  {s.step}
                </div>
              ))}
            </div>
            <div id="donation-step-panel" role="tabpanel" className="p-8 rounded-sm" style={{ background: "#041829", border: "1px solid rgba(7,212,212,0.1)" }}>
              <div className="font-display text-2xl font-700 mb-3">{donationSteps[activeDonation].title}</div>
              <p className="text-sm leading-relaxed mb-8" style={{ color: "#5E8FA5" }}>{donationSteps[activeDonation].desc}</p>

              {activeDonation === 0 && (
                <div className="grid grid-cols-3 gap-3">
                  {["Coral Restoration", "Plastic Removal", "Marine Reserves"].map((c, i) => (
                    <button key={c}
                      className="p-4 text-xs rounded-sm text-center transition-all duration-200 hover:-translate-y-0.5"
                      style={{
                        background: i === 1 ? `${CYAN}20` : "#020D1A",
                        border: `1px solid ${i === 1 ? CYAN : "rgba(7,212,212,0.15)"}`,
                        color: i === 1 ? CYAN : "#5E8FA5",
                      }}
                    >{c}</button>
                  ))}
                </div>
              )}
              {activeDonation === 1 && (
                <div>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {[10, 25, 50, 100, 250].map(a => (
                      <button key={a} onClick={() => setDonationAmt(a)}
                        className="px-5 py-2.5 text-sm rounded-sm transition-all duration-200"
                        style={{
                          background: donationAmt === a ? CYAN : "#020D1A",
                          color: donationAmt === a ? "#020D1A" : "#5E8FA5",
                          border: `1px solid ${donationAmt === a ? CYAN : "rgba(7,212,212,0.15)"}`,
                        }}
                      >${a}</button>
                    ))}
                  </div>
                  <div className="p-4 rounded-sm text-sm" style={{ background: `${CYAN}10`, border: `1px solid ${CYAN}20`, color: CYAN }}>
                    ${donationAmt} = {donationAmt * 2} lbs of ocean plastic removed + {Math.round(donationAmt / 5)} coral fragments planted
                  </div>
                </div>
              )}
              {activeDonation === 2 && (
                <div className="space-y-3">
                  {["Digital Ocean Guardian Badge", "Monthly Impact Report", "Exclusive Campaign Updates", "Name on Ocean Wall of Honor"].map((b, i) => (
                    <div key={b} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-sm flex items-center justify-center flex-shrink-0" style={{ background: `${CYAN}20`, border: `1px solid ${CYAN}` }}>
                        <Check size={11} style={{ color: CYAN }} />
                      </div>
                      <span className="text-sm" style={{ color: i === 0 ? "#E2F0F5" : "#6B9BB5" }}>{b}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </FadeIn>

          {/* Donation card preview */}
          <FadeIn delay={0.2}>
            <div className="rounded-sm overflow-hidden" style={{ border: "1px solid rgba(7,212,212,0.15)" }}>
              <div className="relative h-48 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1465101162946-4377e57745c3?w=700&h=300&fit=crop&auto=format"
                  alt="Vibrant coral reef"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent 0%, #041829 100%)" }} />
                <div className="absolute bottom-4 left-5 right-5">
                  <div className="font-display text-xl font-700">Restore a Coral Reef</div>
                  <div className="text-xs mt-1" style={{ color: "#6B9BB5" }}>Partnered with Reef Check Foundation</div>
                </div>
              </div>
              <div className="p-6" style={{ background: "#041829" }}>
                <div className="flex items-center justify-between mb-6">
                  <div className="font-display text-3xl font-700" style={{ color: CYAN }}>${donationAmt}</div>
                  <div className="font-mono-dm text-[10px] tracking-widest uppercase px-3 py-1 rounded-sm"
                    style={{ background: `${CYAN}15`, color: CYAN, border: `1px solid ${CYAN}30` }}>
                    Monthly
                  </div>
                </div>
                <div className="w-full h-1.5 rounded-full mb-2" style={{ background: "#020D1A" }}>
                  <div className="h-full rounded-full" style={{ width: "67%", background: `linear-gradient(to right, #0B5EA8, ${CYAN})` }} />
                </div>
                <div className="flex justify-between text-[10px] mb-6" style={{ color: "#5E8FA5" }}>
                  <span>$1.6M raised</span><span>Goal: $2.4M</span>
                </div>
                <div className="w-full py-3.5 text-sm font-medium rounded-sm transition-all duration-300 hover:brightness-110 pointer-cursor text-center"
                  style={{ background: `linear-gradient(135deg, #0B5EA8, ${CYAN})`, color: "#020D1A" }}>
                  Donate ${donationAmt} / month
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── INTERACTIVE MAP ── */}
      <section className="px-8 py-20 max-w-6xl mx-auto">
        <FadeIn>
          <Tag>Interactive Map</Tag>
          <h2 className="font-display text-4xl md:text-5xl font-700 mt-6 mb-12 leading-tight">
            The ocean, zone<br />
            <span className="italic" style={{ color: CYAN }}>by zone.</span>
          </h2>
        </FadeIn>
        <FadeIn delay={0.1}>
          <div className="rounded-sm overflow-hidden" style={{ border: "1px solid rgba(7,212,212,0.15)" }}>
            <div className="flex items-center justify-between px-6 py-4" style={{ background: "#041829", borderBottom: "1px solid rgba(7,212,212,0.1)" }}>
              <div className="font-mono-dm text-[10px] tracking-widest uppercase" style={{ color: CYAN }}>Conservation Zone Monitor</div>
              <div className="flex gap-4">
                {[["critical", CORAL], ["protected", CYAN], ["monitoring", "#F0C040"]].map(([label, color]) => (
                  <div key={label} className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full" style={{ background: color }} />
                    <span className="text-[9px] font-mono-dm tracking-widest uppercase" style={{ color: "#5E8FA5" }}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative" style={{ background: "#041829" }}>
              <img
                src="https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?w=1200&h=500&fit=crop&auto=format"
                alt="World map ocean view"
                className="w-full object-cover opacity-30"
                style={{ height: 320 }}
              />
              <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, #020D1A40, #0B2238A0)" }} />
              {/* Ocean zone markers */}
              {mapZones.map((zone, i) => (
                <div
                  key={i}
                  className="absolute cursor-pointer"
                  style={{ left: `${zone.x}%`, top: `${zone.y}%`, transform: "translate(-50%,-50%)" }}
                  onMouseEnter={() => setHoveredZone(i)}
                  onMouseLeave={() => setHoveredZone(null)}
                >
                  <div className="relative flex items-center justify-center">
                    <div className="absolute rounded-full"
                      style={{
                        width: zone.size * 2,
                        height: zone.size * 2,
                        background: `${zoneColor(zone.status)}15`,
                        border: `1px solid ${zoneColor(zone.status)}40`,
                        animation: zone.status === "critical" ? "pulse-ring 2s ease-out infinite" : "none",
                      }}
                    />
                    <div
                      className="w-4 h-4 rounded-full border-2 relative z-10"
                      style={{ background: `${zoneColor(zone.status)}30`, borderColor: zoneColor(zone.status) }}
                    />
                  </div>
                  {hoveredZone === i && (
                    <div
                      className="absolute left-5 -top-8 whitespace-nowrap px-3 py-1.5 rounded-sm text-[10px] font-medium z-20"
                      style={{ background: "#041829", border: `1px solid ${zoneColor(zone.status)}40`, color: "#E2F0F5", boxShadow: "0 4px 20px rgba(0,0,0,0.5)" }}
                    >
                      <MapPin size={9} className="inline mr-1" style={{ color: zoneColor(zone.status) }} />
                      {zone.label}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </FadeIn>
      </section>

      {/* ── VOLUNTEER SIGN-UP ── */}
      <section className="px-8 py-20 max-w-6xl mx-auto">
        <FadeIn>
          <Tag>Volunteer Sign-Up</Tag>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mt-10 items-center">
            <div>
              <h2 className="font-display text-4xl md:text-5xl font-700 leading-tight mb-6">
                Become an<br />
                <span className="italic" style={{ color: CYAN }}>Ocean Guardian.</span>
              </h2>
              <p className="text-sm leading-relaxed mb-8" style={{ color: "#5E8FA5" }}>
                Whether you have two hours or twenty, there is a role for you.
                We match volunteers to local clean-up crews, remote data analysis,
                social media amplification, and offshore monitoring vessels.
              </p>
              <div className="space-y-4">
                {[
                  { role: "Beach Clean-Up Crew", spots: "1,240 open", icon: "🏖️" },
                  { role: "Data & Research Volunteer", spots: "340 open", icon: "🔬" },
                  { role: "Social Ambassador", spots: "5,000 open", icon: "📱" },
                ].map(({ role, spots, icon }) => (
                  <div key={role} className="flex items-center justify-between p-4 rounded-sm"
                    style={{ background: "#041829", border: "1px solid rgba(7,212,212,0.1)" }}>
                    <div className="flex items-center gap-3">
                      <span className="text-lg">{icon}</span>
                      <div>
                        <div className="text-sm font-medium">{role}</div>
                        <div className="text-[10px] font-mono-dm" style={{ color: CYAN }}>{spots}</div>
                      </div>
                    </div>
                    <ChevronRight size={14} style={{ color: "#5E8FA5" }} />
                  </div>
                ))}
              </div>
            </div>

            {/* Sign-up form */}
            <FadeIn delay={0.15}>
              <div className="p-8 rounded-sm" style={{ background: "#041829", border: "1px solid rgba(7,212,212,0.15)" }}>
                {submitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
                      style={{ background: `${CYAN}20`, border: `2px solid ${CYAN}` }}>
                      <Check size={28} style={{ color: CYAN }} />
                    </div>
                    <div className="font-display text-2xl font-700 mb-2">Welcome, Guardian.</div>
                    <p className="text-sm" style={{ color: "#5E8FA5" }}>
                      Check your inbox for your ocean zone assignment and onboarding guide.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="font-display text-2xl font-700 mb-2">Join the mission</div>
                    <p className="text-sm mb-6" style={{ color: "#5E8FA5" }}>Takes 60 seconds. No experience needed.</p>
                    <div className="space-y-3">
                      {[
                        { label: "Full Name", placeholder: "Alex Rivera", type: "text" },
                        { label: "Email Address", placeholder: "you@example.com", type: "email" },
                        { label: "Location / City", placeholder: "Miami, FL", type: "text" },
                      ].map(({ label, placeholder, type }) => (
                        <div key={label}>
                          <label className="block text-[10px] font-mono-dm tracking-widest uppercase mb-2" style={{ color: "#5E8FA5" }}>{label}</label>
                          <input
                            type={type}
                            placeholder={placeholder}
                            value={label === "Email Address" ? email : undefined}
                            onChange={label === "Email Address" ? e => setEmail(e.target.value) : undefined}
                            className="w-full px-4 py-3 text-sm rounded-sm outline-none transition-all duration-200"
                            style={{
                              background: "#020D1A",
                              border: "1px solid rgba(7,212,212,0.2)",
                              color: "#E2F0F5",
                            }}
                            onFocus={e => (e.target.style.borderColor = CYAN)}
                            onBlur={e => (e.target.style.borderColor = "rgba(7,212,212,0.2)")}
                          />
                        </div>
                      ))}
                      <div>
                        <label className="block text-[10px] font-mono-dm tracking-widest uppercase mb-2" style={{ color: "#5E8FA5" }}>Preferred Role</label>
                        <select
                          className="w-full px-4 py-3 text-sm rounded-sm outline-none"
                          style={{ background: "#020D1A", border: "1px solid rgba(7,212,212,0.2)", color: "#E2F0F5" }}
                        >
                          <option>Beach Clean-Up Crew</option>
                          <option>Data & Research Volunteer</option>
                          <option>Social Ambassador</option>
                        </select>
                      </div>
                      <div
                        onClick={() => setSubmitted(true)}
                        className="w-full py-3.5 text-sm font-medium rounded-sm mt-2 transition-all duration-300 hover:brightness-110 hover:scale-[1.02] pointer-cursor text-center"
                        style={{ background: CYAN, color: "#020D1A" }}
                      >
                        Become a Guardian
                      </div>
                    </div>
                  </>
                )}
              </div>
            </FadeIn>
          </div>
        </FadeIn>
      </section>

      {/* ── SOCIAL MEDIA ASSETS ── */}
      <section className="px-8 py-20 max-w-6xl mx-auto">
        <FadeIn>
          <Tag>Social Media Assets</Tag>
          <h2 className="font-display text-4xl md:text-5xl font-700 mt-6 mb-12 leading-tight">
            32 assets. One<br />
            <span className="italic" style={{ color: CYAN }}>unified voice.</span>
          </h2>
        </FadeIn>
        <FadeIn delay={0.1}>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {socialAssets.map(({ url, caption, platform, type }, i) => (
              <div key={url}
                className="rounded-sm overflow-hidden group cursor-default"
                style={{ border: "1px solid rgba(7,212,212,0.1)" }}
              >
                <div className="relative overflow-hidden" style={{ height: 220 }}>
                  <img
                    src={url}
                    alt={caption}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent 40%, #020D1AE0 100%)" }} />
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <div className="font-mono-dm text-[8px] tracking-widest uppercase mb-1" style={{ color: CYAN }}>{platform} · {type}</div>
                    <p className="text-[10px] leading-snug" style={{ color: "#C5DEE8" }}>{caption}</p>
                  </div>
                  {/* Platform icon overlay */}
                  <div className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center"
                    style={{ background: "rgba(2,13,26,0.7)", border: `1px solid ${CYAN}30` }}>
                    {platform === "Instagram" ? <Camera size={12} style={{ color: CYAN }} /> :
                      platform === "Twitter" ? <Bird size={12} style={{ color: CYAN }} /> :
                        <Globe size={12} style={{ color: CYAN }} />}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </FadeIn>
      </section>

      {/* ── IMPACT TYPOGRAPHY ── */}
      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1518020382113-a7e8fc38eac9?w=1800&h=600&fit=crop&auto=format"
            alt="Ocean deep"
            className="w-full h-full object-cover opacity-10"
          />
        </div>
        <div className="relative z-10 px-8 max-w-6xl mx-auto text-center">
          <FadeIn>
            <div className="font-display text-[clamp(5rem,15vw,14rem)] font-900 leading-none tracking-tighter shimmer-text">
              NOW.
            </div>
            <p className="font-display text-2xl md:text-3xl italic mt-4" style={{ color: "#6B9BB5" }}>
              The ocean cannot wait for tomorrow.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ── OUTCOMES ── */}
      <section id="outcomes" className="px-8 py-28 max-w-6xl mx-auto">
        <FadeIn>
          <Tag>Campaign Outcomes</Tag>
          <h2 className="font-display text-4xl md:text-5xl font-700 mt-6 mb-16 leading-tight">
            Numbers that<br />
            <span className="italic" style={{ color: CYAN }}>moved the needle.</span>
          </h2>
        </FadeIn>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {outcomes.map(({ metric, label, change }, i) => (
            <FadeIn key={label} delay={i * 0.07}>
              <div className="p-8 rounded-sm" style={{ background: "#041829", border: "1px solid rgba(7,212,212,0.12)" }}>
                <div className="font-display text-4xl md:text-5xl font-900 leading-none mb-3" style={{ color: CYAN }}>{metric}</div>
                <div className="text-sm font-medium mb-2">{label}</div>
                <div className="font-mono-dm text-[10px] tracking-widest" style={{ color: "#4CAF7D" }}>{change} vs. baseline</div>
              </div>
            </FadeIn>
          ))}
        </div>

        {/* Closing quote */}
        <FadeIn delay={0.15} className="mt-20">
          <div className="p-10 rounded-sm relative overflow-hidden" style={{ background: "#041829", border: `1px solid ${CYAN}20` }}>
            <div className="absolute top-0 left-0 w-1 h-full" style={{ background: `linear-gradient(to bottom, ${CYAN}, #0B5EA8)` }} />
            <div className="font-display text-6xl leading-none mb-4" style={{ color: CYAN }}>"</div>
            <p className="font-display text-xl md:text-2xl italic leading-relaxed mb-6" style={{ color: "#C5DEE8" }}>
              Project Ocean did not just raise money — it built a community of people
              who will protect our seas for a generation. The design made us believe
              it was possible.
            </p>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full overflow-hidden">
                <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&auto=format" alt="Client" className="w-full h-full object-cover" />
              </div>
              <div>
                <div className="text-sm font-medium">Dr. Marina Vasquez</div>
                <div className="font-mono-dm text-[10px] tracking-wide" style={{ color: "#5E8FA5" }}>Executive Director, Project Ocean NGO</div>
              </div>
            </div>
          </div>
        </FadeIn>
      </section>

      {/* ── FOOTER ── */}
      </main>
      <footer className="px-8 py-16 mt-10" style={{ borderTop: "1px solid rgba(7,212,212,0.1)" }}>
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: `${CYAN}20`, border: `1.5px solid ${CYAN}` }}>
              <Droplets size={16} style={{ color: CYAN }} />
            </div>
            <div>
              <div className="font-display text-sm font-700">Project Ocean</div>
              <div className="font-mono-dm text-[9px] tracking-widest uppercase" style={{ color: "#5E8FA5" }}>Portfolio Case Study · 2025</div>
            </div>
          </div>
          <div className="text-center">
            <p className="text-xs" style={{ color: "#5E8FA5" }}>
              Designed with purpose. Built for impact. Every pixel in service of the sea.
            </p>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/" className="text-xs font-medium" style={{ color: CYAN }}>Back to portfolio</Link>
            {[Camera, Bird, Mail].map((Icon, i) => (
              <button key={i}
                type="button"
                aria-label={`Preview ${["Instagram", "Twitter", "email"][i]} campaign concept`}
                className="w-9 h-9 rounded-sm flex items-center justify-center transition-all duration-300 hover:-translate-y-0.5"
                style={{ background: "#041829", border: "1px solid rgba(7,212,212,0.15)" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = CYAN; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(7,212,212,0.15)"; }}
              >
                <Icon size={14} style={{ color: "#5E8FA5" }} />
              </button>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
